// views/Dashboard/PainelExecutivo/areas/AreaCeo.tsx
//
// A aba do CEO (pedido do Igor em 19/10/2026), um bloco por pergunta dele, na
// ordem em que ele perguntou:
//
//   1. as usinas estão gerando a meta?            → Geração (este mês / mês a mês)
//   2. para onde vai a energia de cada usina?     → produziu, própria, outros, sobrando
//   3. quanto tempo pararam, e por quê?           → Paradas, com o motivo
//   4. cada usina bateu a meta de faturamento?    → Faturamento por usina
//   5. os departamentos batem os KPIs?            → KPIs, já calculados
//   6. quanto entrou, quanto vai entrar, quem não pagou? → Dinheiro (por último, a pedido)
//
// Cada título já diz a conclusão; o detalhe fica nas abas de cada área.
import React, { useMemo, useState } from 'react';
import DinheiroDoPeriodo from '../sections/ceo/DinheiroDoPeriodo';
import GeracaoDoMes from '../sections/ceo/GeracaoDoMes';
import EnergiaDasUsinas from '../sections/ceo/EnergiaDasUsinas';
import ParadasDoMes from '../sections/ceo/ParadasDoMes';
import FaturamentoPorUsina from '../sections/ceo/FaturamentoPorUsina';
import KpisDepartamentos from '../sections/ceo/KpisDepartamentos';
import {
  MESES_HISTORIA_GERACAO, useGeracaoCeo, useMesCorrente, useMetasFaturamento, useParadasCeo, useUsinasCeo,
} from '../sections/ceo/useCeoDados';
import { useContextoKpis } from '../sections/ceo/useContextoKpis';
import { useFinanceiroDashboardData } from '../sections/financeiro/useFinanceiroDashboardData';
import { agruparParadas, ultimoMesFechado } from '../../../../src/lib/dashboard/painelCeo';

const AreaCeo: React.FC = () => {
  const { agora, inicio, fim } = useMesCorrente();
  const { usinas, fator, loading: carregandoUsinas, erro: erroUsinas } = useUsinasCeo();
  const geracao = useGeracaoCeo(usinas, inicio, fim);
  const paradas = useParadasCeo(usinas, inicio, agora);
  const metas = useMetasFaturamento();
  const { dados } = useFinanceiroDashboardData();
  const nomeMes = agora.toLocaleDateString('pt-BR', { month: 'long' });

  const eventos = useMemo(() => agruparParadas(paradas.episodios, usinas), [paradas.episodios, usinas]);

  // Energia e faturamento olham o MESMO mês de contas, e andam juntos.
  const fechado = useMemo(() => ultimoMesFechado(dados, agora), [dados, agora]);
  const [mesEscolhido, setMesEscolhido] = useState<string | null>(null);
  const mesContas = mesEscolhido ?? fechado;
  const ordAtual = agora.getFullYear() * 12 + agora.getMonth();
  const ordMinimo = ordAtual - (MESES_HISTORIA_GERACAO - 1);

  const ctx = useContextoKpis({
    agora, usinas, fator, linhas: geracao.linhas, eventos, paradasCarregando: paradas.loading, mesFechado: fechado, metas,
  });

  return (
    <div className="space-y-5">
      <p className="text-[11px] text-gray-400 dark:text-gray-500">
        Atualizado às {agora.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })} · dados de produção · a parte financeira se atualiza sozinha a cada 10 minutos
      </p>

      <GeracaoDoMes
        usinas={usinas}
        fator={fator}
        linhas={geracao.linhas}
        diarios={geracao.diarios}
        paradasAgora={geracao.paradasAgora}
        eventos={eventos}
        loading={carregandoUsinas || geracao.loading}
        erro={erroUsinas || geracao.erro}
        agora={agora}
      />

      <EnergiaDasUsinas
        usinas={usinas}
        fator={fator}
        linhas={geracao.linhas}
        mes={mesContas}
        onMes={setMesEscolhido}
        ordMinimo={ordMinimo}
        ordAtual={ordAtual}
        carregando={carregandoUsinas || geracao.loading}
      />

      <ParadasDoMes eventos={eventos} usinas={usinas} loading={carregandoUsinas || paradas.loading} erro={paradas.erro} nomeMes={nomeMes} />

      <FaturamentoPorUsina
        usinas={usinas}
        carregandoUsinas={carregandoUsinas}
        metas={metas}
        mes={mesContas}
        onMes={setMesEscolhido}
        fechado={fechado}
        ordMinimo={ordMinimo}
        ordAtual={ordAtual}
      />

      <KpisDepartamentos ctx={ctx} />

      <DinheiroDoPeriodo agora={agora} />
    </div>
  );
};

export default AreaCeo;
